/**
 * Mehrfachauswahl-Widget der Spaltenfilter (Auftrag "Bestellboard-Filter datentypgerecht bauen",
 * 2026-09-01) - NEUBAU ohne Bestandsvorbild: `index.html` kennt nur ein natives `<select>` mit
 * Exakttreffer (siehe Kopfkommentar `Spaltenkopf.tsx`). Traegt sowohl die feste kleine Aufzaehlung
 * "Status" (`orderListFilterKind` -> "enum") als auch die "Freitext mit Wertevorrat"-Spalten
 * (Lieferant, Bestellnummer, Custom-Felder - `orderListFilterKind` -> "text"): beide sind fachlich
 * dieselbe Bedienung, nur die Herkunft der Optionsliste unterscheidet sich (kanonisch vs. aus
 * `facetOrders` abgeleitet) - das entscheidet der Aufrufer (`Spaltenkopf.tsx`), nicht dieses Widget.
 *
 * Das Suchfeld erscheint nur ab `SUCH_SCHWELLE` Optionen (Auftragsbrief: "Suchfeld bei vielen
 * Optionen") - bei der sechs-/siebenwertigen Status-Aufzaehlung waere es reine Bedienlast.
 */
import { useState } from "react";
import type { OrderListFacet } from "../../../lib/orderList.ts";

/** Ab dieser Optionenzahl blendet das Widget ein Suchfeld ein. */
export const SUCH_SCHWELLE = 8;

export interface MehrfachauswahlFilterProps {
  /** Beschriftung fuer `aria-label`/Platzhalter - i. d. R. der Spaltenname. */
  label: string;
  optionen: readonly OrderListFacet[];
  ausgewaehlt: readonly string[];
  onAendern: (werte: string[]) => void;
}

export function MehrfachauswahlFilter({
  label,
  optionen,
  ausgewaehlt,
  onAendern,
}: MehrfachauswahlFilterProps) {
  const [suche, setzeSuche] = useState("");
  const sichtbareOptionen =
    suche.trim() === ""
      ? optionen
      : optionen.filter((option) => option.label.toLowerCase().includes(suche.trim().toLowerCase()));

  const toggeln = (wert: string): void => {
    onAendern(
      ausgewaehlt.includes(wert) ? ausgewaehlt.filter((v) => v !== wert) : [...ausgewaehlt, wert],
    );
  };

  return (
    <div className="spaltenfilter-mehrfachauswahl">
      {optionen.length > SUCH_SCHWELLE ? (
        <input
          type="text"
          className="spaltenfilter-suche"
          placeholder="Suchen…"
          aria-label={`${label}-Optionen durchsuchen`}
          value={suche}
          onChange={(ereignis) => setzeSuche(ereignis.target.value)}
        />
      ) : null}
      <div className="spaltenfilter-optionen" role="group" aria-label={`${label}-Optionen`}>
        {sichtbareOptionen.length ? (
          sichtbareOptionen.map((option) => (
            <label key={option.value} className="spaltenfilter-option">
              <input
                type="checkbox"
                // Eigenes `aria-label` MIT Spaltenkontext statt des blossen Options-Textes (Pruefung
                // K1): `BoardSeite` bleibt app-weit dauerhaft montiert (`App.tsx`), ein Optionslabel
                // wie eine Bestellnummer ("BE-1") kollidiert sonst mit einem gleichlautenden
                // `aria-label` andernorts (`RatenSammelDialog`) - `getByRole("checkbox", {name:
                // /BE-1/})` faende dann zwei Elemente. Der sichtbare Text (`<span>`) bleibt
                // unveraendert nur die Beschriftung, ohne Spaltennamen-Praefix.
                aria-label={`${label}: ${option.label}`}
                checked={ausgewaehlt.includes(option.value)}
                onChange={() => toggeln(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))
        ) : (
          <p className="spaltenfilter-keine-treffer">Keine Treffer.</p>
        )}
      </div>
    </div>
  );
}
